/**
 * Referral (09) i18n — FR default + AR-RTL. Registered once in the module's onRegister(). Keys are
 * namespaced under `referral.*` and `nav.referral.*`. Layout mirrors automatically under dir="rtl"
 * via CSS logical properties; codes / emails / percentages stay LTR with .nv-ltr-island.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.referral": "Médecins prescripteurs",
  "nav.referral.liens": "Liens médecins",
  "nav.referral.codes": "Codes & analytics",
  "nav.referral.retours": "Retours prescripteur",

  // shared
  "referral.unavailable.title": "Module indisponible",
  "referral.unavailable.desc":
    "L'attribution prescripteur n'est disponible que sur les installations cloud. Ce module n'est pas activé sur cette installation locale.",
  "referral.action.cancel": "Annuler",
  "referral.action.refresh": "Actualiser",
  "referral.field.required": "Champ requis.",
  "referral.field.emailInvalid": "Adresse e-mail invalide.",
  "referral.field.pctInvalid": "Pourcentage invalide (0–100).",
  "referral.medecin.pick": "Médecin",
  "referral.medecin.pick.placeholder": "Choisir un médecin…",

  // ── Liens (link table) ──────────────────────────────────────────────────────────
  "referral.liens.title": "Liens lab ↔ médecin",
  "referral.liens.subtitle": "Invitez un médecin, suivez l'approbation, révoquez le lien.",
  "referral.liens.invite": "Inviter un médecin",
  "referral.liens.empty.title": "Aucun médecin",
  "referral.liens.empty.desc": "Invitez votre premier médecin prescripteur pour démarrer l'attribution.",
  "referral.liens.col.nom": "Médecin",
  "referral.liens.col.email": "E-mail Novera",
  "referral.liens.col.statut": "Statut du lien",
  "referral.liens.col.retours": "Retours",
  "referral.liens.col.actions": "Actions",
  "referral.liens.action.decision": "Décision",
  "referral.liens.action.revoke": "Révoquer",

  // lien statut chips
  "referral.lien.statut.invite": "Invité",
  "referral.lien.statut.approuve": "Approuvé",
  "referral.lien.statut.rejete": "Refusé",
  "referral.lien.statut.revoque": "Révoqué",
  "referral.lien.statut.none": "Aucun lien",

  // invite modal
  "referral.invite.title": "Inviter un médecin",
  "referral.invite.nom": "Nom",
  "referral.invite.prenom": "Prénom",
  "referral.invite.specialite": "Spécialité",
  "referral.invite.email": "E-mail Novera",
  "referral.invite.email.hint": "L'adresse Novera du médecin recevra l'invitation seam-2.",
  "referral.invite.remise": "Remise référral (%)",
  "referral.invite.remise.hint": "Remise par défaut appliquée aux dossiers attribués (0–100).",
  "referral.invite.submit": "Envoyer l'invitation",
  "referral.invite.success": "Invitation envoyée à {nom}.",
  "referral.invite.token.title": "Jeton de rappel (seam-2)",
  "referral.invite.token.desc":
    "Conservez ce jeton à usage unique : il permet d'appliquer la décision du médecin.",

  // decision modal
  "referral.decision.title": "Appliquer la décision",
  "referral.decision.desc":
    "Décision seam-2 résolue par le jeton de rappel à usage unique (normalement transmis par la plateforme Novera).",
  "referral.decision.token": "Jeton de rappel",
  "referral.decision.token.hint": "Le jeton à usage unique reçu à l'invitation.",
  "referral.decision.choice": "Décision",
  "referral.decision.approve": "Approuver",
  "referral.decision.refuse": "Refuser",
  "referral.decision.noveraRef": "Référence Novera",
  "referral.decision.noveraRef.hint": "Identifiant central du médecin (facultatif, sur approbation).",
  "referral.decision.submit": "Appliquer",
  "referral.decision.success": "Décision appliquée : {statut}.",

  // revoke modal
  "referral.revoke.title": "Révoquer le lien",
  "referral.revoke.desc":
    "La révocation archive le lien et désactive tous les codes du médecin (irréversible côté lien).",
  "referral.revoke.medecin": "Médecin",
  "referral.revoke.motif": "Motif",
  "referral.revoke.motif.hint": "Raison de la révocation (tracée).",
  "referral.revoke.submit": "Révoquer le lien",
  "referral.revoke.success": "Lien révoqué — {n} code(s) désactivé(s).",

  // ── Codes & analytics ───────────────────────────────────────────────────────────
  "referral.codes.title": "Codes référral & analytics",
  "referral.codes.subtitle": "Suivez les médecins approuvés, leurs retours, et gérez leurs codes.",
  "referral.codes.new": "Nouveau code",
  "referral.codes.tab.analytics": "Analytics",
  "referral.codes.tab.manage": "Gestion des codes",
  "referral.codes.empty.title": "Aucun médecin",
  "referral.codes.empty.desc": "Approuvez un lien médecin pour créer des codes référral.",

  // analytics tiles / cards
  "referral.analytics.kpi.medecins": "Médecins",
  "referral.analytics.kpi.approuves": "Liens approuvés",
  "referral.analytics.kpi.invites": "Invitations en attente",
  "referral.analytics.kpi.retours": "Retours attribués",
  "referral.analytics.chart.title": "Retours par médecin",
  "referral.analytics.chart.empty": "Aucun retour attribué pour le moment.",
  "referral.analytics.col.nom": "Médecin",
  "referral.analytics.col.email": "E-mail",
  "referral.analytics.col.statut": "Lien",
  "referral.analytics.col.retours": "Retours",
  "referral.analytics.col.candidate": "Remise active",
  "referral.analytics.col.actions": "Actions",
  "referral.analytics.action.code": "Créer un code",
  "referral.analytics.candidate.none": "Aucune",
  "referral.analytics.candidate.loading": "…",

  // code form modal
  "referral.code.title": "Nouveau code référral",
  "referral.code.medecin": "Médecin",
  "referral.code.medecin.hint": "Seuls les médecins au lien approuvé peuvent recevoir un code.",
  "referral.code.code": "Code",
  "referral.code.code.hint": "Identifiant unique du code (ex. DRX-2026).",
  "referral.code.libelle": "Libellé",
  "referral.code.remise": "Remise (%)",
  "referral.code.remise.hint": "Override de la remise du lien (facultatif, 0–100).",
  "referral.code.expire": "Expire le",
  "referral.code.submit": "Créer le code",
  "referral.code.success": "Code {code} créé.",
  "referral.code.needLink": "Un lien approuvé est requis pour ce médecin.",

  // toggle
  "referral.code.toggle.activate": "Activer",
  "referral.code.toggle.deactivate": "Désactiver",
  "referral.code.toggle.success": "Statut du code mis à jour : {statut}.",

  // ── Retours (prescriber-return tracking) ────────────────────────────────────────
  "referral.retours.title": "Retours prescripteur",
  "referral.retours.subtitle": "Suivi de la livraison des comptes rendus au dossier Novera du médecin.",
  "referral.retours.pick.title": "Sélectionnez un médecin",
  "referral.retours.pick.desc": "Choisissez un médecin pour afficher ses retours de comptes rendus.",
  "referral.retours.empty.title": "Aucun retour",
  "referral.retours.empty.desc": "Aucun compte rendu n'a encore été retourné à ce médecin.",
  "referral.retours.col.demande": "Demande",
  "referral.retours.col.cr": "Compte rendu",
  "referral.retours.col.version": "Version",
  "referral.retours.col.params": "Paramètres validés",
  "referral.retours.col.critique": "Critique",
  "referral.retours.col.canal": "Canal",
  "referral.retours.col.statut": "Statut",
  "referral.retours.col.date": "Retourné le",
  "referral.retours.critique.yes": "Oui",
  "referral.retours.critique.no": "Non",
  "referral.retours.kpi.total": "Total",
  "referral.retours.kpi.envoye": "Envoyés",
  "referral.retours.kpi.attente": "En attente",

  // retour statut chips
  "referral.retour.statut.en_attente": "En attente",
  "referral.retour.statut.envoye": "Envoyé",
  "referral.retour.statut.echec": "Échec",

  // canal labels
  "referral.canal.novera_dossier": "Dossier Novera",
  "referral.canal.email": "E-mail",
  "referral.canal.queued": "En file",
};

const AR: Record<string, string> = {
  // nav
  "nav.section.referral": "الأطباء الواصفون",
  "nav.referral.liens": "روابط الأطباء",
  "nav.referral.codes": "الرموز والتحليلات",
  "nav.referral.retours": "إرجاع الواصف",

  // shared
  "referral.unavailable.title": "الوحدة غير متوفرة",
  "referral.unavailable.desc":
    "إسناد الواصف متاح فقط على التثبيتات السحابية. هذه الوحدة غير مُفعّلة على هذا التثبيت المحلي.",
  "referral.action.cancel": "إلغاء",
  "referral.action.refresh": "تحديث",
  "referral.field.required": "حقل مطلوب.",
  "referral.field.emailInvalid": "البريد الإلكتروني غير صالح.",
  "referral.field.pctInvalid": "نسبة غير صالحة (0–100).",
  "referral.medecin.pick": "الطبيب",
  "referral.medecin.pick.placeholder": "اختر طبيبًا…",

  // liens
  "referral.liens.title": "روابط المخبر ↔ الطبيب",
  "referral.liens.subtitle": "ادعُ طبيبًا، تابِع الموافقة، وألغِ الرابط.",
  "referral.liens.invite": "دعوة طبيب",
  "referral.liens.empty.title": "لا يوجد أطباء",
  "referral.liens.empty.desc": "ادعُ أول طبيب واصف لبدء الإسناد.",
  "referral.liens.col.nom": "الطبيب",
  "referral.liens.col.email": "بريد Novera",
  "referral.liens.col.statut": "حالة الرابط",
  "referral.liens.col.retours": "الإرجاعات",
  "referral.liens.col.actions": "إجراءات",
  "referral.liens.action.decision": "قرار",
  "referral.liens.action.revoke": "إلغاء",

  "referral.lien.statut.invite": "مدعوّ",
  "referral.lien.statut.approuve": "مُعتمد",
  "referral.lien.statut.rejete": "مرفوض",
  "referral.lien.statut.revoque": "مُلغى",
  "referral.lien.statut.none": "لا يوجد رابط",

  "referral.invite.title": "دعوة طبيب",
  "referral.invite.nom": "الاسم",
  "referral.invite.prenom": "اللقب",
  "referral.invite.specialite": "التخصص",
  "referral.invite.email": "بريد Novera",
  "referral.invite.email.hint": "سيتلقى عنوان Novera الخاص بالطبيب الدعوة.",
  "referral.invite.remise": "خصم الإحالة (%)",
  "referral.invite.remise.hint": "الخصم الافتراضي على الملفات المُسندة (0–100).",
  "referral.invite.submit": "إرسال الدعوة",
  "referral.invite.success": "أُرسلت الدعوة إلى {nom}.",
  "referral.invite.token.title": "رمز الاستدعاء (seam-2)",
  "referral.invite.token.desc": "احتفظ بهذا الرمز ذي الاستخدام الواحد لتطبيق قرار الطبيب.",

  "referral.decision.title": "تطبيق القرار",
  "referral.decision.desc": "قرار seam-2 يُحلّ عبر رمز الاستدعاء ذي الاستخدام الواحد.",
  "referral.decision.token": "رمز الاستدعاء",
  "referral.decision.token.hint": "الرمز ذو الاستخدام الواحد المُستلم عند الدعوة.",
  "referral.decision.choice": "القرار",
  "referral.decision.approve": "موافقة",
  "referral.decision.refuse": "رفض",
  "referral.decision.noveraRef": "مرجع Novera",
  "referral.decision.noveraRef.hint": "المعرّف المركزي للطبيب (اختياري عند الموافقة).",
  "referral.decision.submit": "تطبيق",
  "referral.decision.success": "تم تطبيق القرار: {statut}.",

  "referral.revoke.title": "إلغاء الرابط",
  "referral.revoke.desc": "الإلغاء يؤرشف الرابط ويعطّل كل رموز الطبيب.",
  "referral.revoke.medecin": "الطبيب",
  "referral.revoke.motif": "السبب",
  "referral.revoke.motif.hint": "سبب الإلغاء (مُسجّل).",
  "referral.revoke.submit": "إلغاء الرابط",
  "referral.revoke.success": "أُلغي الرابط — {n} رمز مُعطّل.",

  // codes & analytics
  "referral.codes.title": "رموز الإحالة والتحليلات",
  "referral.codes.subtitle": "تابِع الأطباء المعتمدين وإرجاعاتهم وأدِر رموزهم.",
  "referral.codes.new": "رمز جديد",
  "referral.codes.tab.analytics": "التحليلات",
  "referral.codes.tab.manage": "إدارة الرموز",
  "referral.codes.empty.title": "لا يوجد أطباء",
  "referral.codes.empty.desc": "اعتمِد رابط طبيب لإنشاء رموز الإحالة.",

  "referral.analytics.kpi.medecins": "الأطباء",
  "referral.analytics.kpi.approuves": "روابط مُعتمدة",
  "referral.analytics.kpi.invites": "دعوات معلّقة",
  "referral.analytics.kpi.retours": "إرجاعات مُسندة",
  "referral.analytics.chart.title": "الإرجاعات حسب الطبيب",
  "referral.analytics.chart.empty": "لا توجد إرجاعات مُسندة بعد.",
  "referral.analytics.col.nom": "الطبيب",
  "referral.analytics.col.email": "البريد",
  "referral.analytics.col.statut": "الرابط",
  "referral.analytics.col.retours": "الإرجاعات",
  "referral.analytics.col.candidate": "الخصم الفعّال",
  "referral.analytics.col.actions": "إجراءات",
  "referral.analytics.action.code": "إنشاء رمز",
  "referral.analytics.candidate.none": "لا شيء",
  "referral.analytics.candidate.loading": "…",

  "referral.code.title": "رمز إحالة جديد",
  "referral.code.medecin": "الطبيب",
  "referral.code.medecin.hint": "فقط الأطباء ذوو الرابط المعتمد يمكنهم تلقّي رمز.",
  "referral.code.code": "الرمز",
  "referral.code.code.hint": "معرّف فريد للرمز (مثال DRX-2026).",
  "referral.code.libelle": "التسمية",
  "referral.code.remise": "الخصم (%)",
  "referral.code.remise.hint": "تجاوز خصم الرابط (اختياري، 0–100).",
  "referral.code.expire": "ينتهي في",
  "referral.code.submit": "إنشاء الرمز",
  "referral.code.success": "أُنشئ الرمز {code}.",
  "referral.code.needLink": "يلزم رابط معتمد لهذا الطبيب.",

  "referral.code.toggle.activate": "تفعيل",
  "referral.code.toggle.deactivate": "تعطيل",
  "referral.code.toggle.success": "تم تحديث حالة الرمز: {statut}.",

  // retours
  "referral.retours.title": "إرجاع الواصف",
  "referral.retours.subtitle": "تتبّع تسليم التقارير إلى ملف Novera الخاص بالطبيب.",
  "referral.retours.pick.title": "اختر طبيبًا",
  "referral.retours.pick.desc": "اختر طبيبًا لعرض إرجاعات تقاريره.",
  "referral.retours.empty.title": "لا توجد إرجاعات",
  "referral.retours.empty.desc": "لم يُرجَع أي تقرير إلى هذا الطبيب بعد.",
  "referral.retours.col.demande": "الطلب",
  "referral.retours.col.cr": "التقرير",
  "referral.retours.col.version": "النسخة",
  "referral.retours.col.params": "المعايير المُصادق عليها",
  "referral.retours.col.critique": "حرِج",
  "referral.retours.col.canal": "القناة",
  "referral.retours.col.statut": "الحالة",
  "referral.retours.col.date": "أُرجِع في",
  "referral.retours.critique.yes": "نعم",
  "referral.retours.critique.no": "لا",
  "referral.retours.kpi.total": "الإجمالي",
  "referral.retours.kpi.envoye": "مُرسَلة",
  "referral.retours.kpi.attente": "قيد الانتظار",

  "referral.retour.statut.en_attente": "قيد الانتظار",
  "referral.retour.statut.envoye": "مُرسَل",
  "referral.retour.statut.echec": "فشل",

  "referral.canal.novera_dossier": "ملف Novera",
  "referral.canal.email": "بريد إلكتروني",
  "referral.canal.queued": "في الطابور",
};

let registered = false;

/** Register the FR + AR referral strings (idempotent — safe to call from onRegister and from tests). */
export function registerReferralMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
